/**
 * TermsAcceptanceModal
 *
 * Displays Terms of Service and Privacy Policy checkboxes.
 * Both must be checked before the user can proceed.
 * Uses focus trap and proper ARIA attributes.
 */

import { useState, useEffect, useRef } from 'react';
import Button from './ui/Button';

interface TermsAcceptanceModalProps {
  onAccept: () => void;
  onDecline: () => void;
  isAccepting: boolean;
  error: string | null;
}

export default function TermsAcceptanceModal({
  onAccept,
  onDecline,
  isAccepting,
  error,
}: TermsAcceptanceModalProps) {
  const [tosChecked, setTosChecked] = useState(false);
  const [privacyChecked, setPrivacyChecked] = useState(false);
  const dialogRef = useRef<HTMLDivElement>(null);
  const firstFocusRef = useRef<HTMLInputElement>(null);

  const canAccept = tosChecked && privacyChecked && !isAccepting;

  // Focus trap and escape key
  useEffect(() => {
    firstFocusRef.current?.focus();

    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        onDecline();
        return;
      }

      if (e.key === 'Tab' && dialogRef.current) {
        const focusable = dialogRef.current.querySelectorAll<HTMLElement>(
          'input, button, a[href], [tabindex]:not([tabindex="-1"])'
        );
        const first = focusable[0];
        const last = focusable[focusable.length - 1];

        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    }

    document.addEventListener('keydown', handleKeyDown);
    // Prevent background scroll
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [onDecline]);

  return (
    <div
      className="fixed inset-0 bg-slate-900/50 flex items-center justify-center p-4 z-50"
      onClick={onDecline}
      role="presentation"
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="terms-modal-title"
        className="bg-white rounded-xl shadow-lg max-w-lg w-full p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <h2
          id="terms-modal-title"
          className="text-lg font-semibold text-slate-950 mb-4"
        >
          Terms of Service and Privacy Policy
        </h2>

        <p className="text-base leading-7 text-slate-800 mb-6">
          Before continuing, please review and accept our Terms of Service and
          Privacy Policy.
        </p>

        <div className="space-y-4 mb-6">
          <label className="flex items-start gap-3 cursor-pointer min-h-[44px]">
            <input
              ref={firstFocusRef}
              type="checkbox"
              checked={tosChecked}
              onChange={(e) => setTosChecked(e.target.checked)}
              className="mt-1 h-5 w-5 rounded border-slate-300 text-emerald-700 focus:ring-emerald-500 focus:ring-2 shrink-0"
            />
            <span className="text-sm text-slate-800">
              I have read and agree to the{' '}
              <a
                href="/legal#terms"
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-700 underline hover:text-blue-800"
              >
                Terms of Service
              </a>
            </span>
          </label>

          <label className="flex items-start gap-3 cursor-pointer min-h-[44px]">
            <input
              type="checkbox"
              checked={privacyChecked}
              onChange={(e) => setPrivacyChecked(e.target.checked)}
              className="mt-1 h-5 w-5 rounded border-slate-300 text-emerald-700 focus:ring-emerald-500 focus:ring-2 shrink-0"
            />
            <span className="text-sm text-slate-800">
              I have read and agree to the{' '}
              <a
                href="/legal#privacy"
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-700 underline hover:text-blue-800"
              >
                Privacy Policy
              </a>
            </span>
          </label>
        </div>

        {error && (
          <div
            className="mb-4 border-l-4 border-red-600 bg-red-50 p-3"
            role="alert"
          >
            <p className="text-sm font-medium text-red-700">{error}</p>
          </div>
        )}

        <div className="flex flex-col sm:flex-row gap-3">
          <Button
            onClick={onAccept}
            disabled={!canAccept}
            loading={isAccepting}
            loadingText="Accepting..."
          >
            Accept and continue
          </Button>
          <Button variant="secondary" onClick={onDecline} disabled={isAccepting}>
            Decline
          </Button>
        </div>
      </div>
    </div>
  );
}
